<template>
  <div
    class="lpu-doctors-filter d-flex"
    :class="isMobile ? 'flex-column' : 'flex-row ga-4 align-center'"
    data-qa="lpu_doctors_list_filter"
  >
    <v-select
      v-model="filter.speciality"
      :items="specialities"
      item-text="name"
      item-value="id"
      return-object
      outlined
      hide-details
      persistent-placeholder
      :placeholder="tStatic('Специальность')"
      append-icon="ui-icon-arrow-down"
      :menu-props="selectMenuProps"
      class="lpu-doctors-filter__select"
      :class="{ 'lpu-doctors-filter__select--specialty': !isMobile }"
      data-qa="lpu_doctors_list_filter_speciality"
    />

    <template v-if="!isMobile">
      <v-select
        v-model="filter.experience"
        :items="experienceItems"
        item-text="name"
        item-value="value"
        outlined
        hide-details
        persistent-placeholder
        :placeholder="tStatic('Стаж')"
        append-icon="ui-icon-arrow-down"
        :menu-props="selectMenuProps"
        class="lpu-doctors-filter__select lpu-doctors-filter__select--experience"
        data-qa="lpu_doctors_list_filter_experience"
      />

      <v-btn-toggle
        v-model="selectedGender"
        borderless
        class="lpu-doctors-filter__gender elevation-0"
        data-qa="lpu_doctors_list_filter_gender"
      >
        <v-btn
          value="M"
          height="48"
          text
          data-qa="lpu_doctors_list_filter_gender_m"
        >
          <span class="ui-icon-gender-male ui-icon-fz-big primary--text" />
        </v-btn>
        <v-btn
          value="F"
          height="48"
          text
          data-qa="lpu_doctors_list_filter_gender_f"
        >
          <span class="ui-icon-gender-female ui-icon-fz-big special--text" />
        </v-btn>
      </v-btn-toggle>

      <div
        class="lpu-doctors-filter__home d-flex align-center px-4"
        role="checkbox"
        tabindex="0"
        :aria-checked="filter.isHomeCall"
        data-qa="lpu_doctors_list_filter_home"
        @click="toggleHomeCall"
        @keydown.enter.prevent="toggleHomeCall"
        @keydown.space.prevent="toggleHomeCall"
      >
        <v-checkbox
          :input-value="filter.isHomeCall"
          hide-details
          color="primary"
          tabindex="-1"
          class="lpu-doctors-filter__home-checkbox ma-0 mt-0 pa-0"
        />
        <span class="text-body-1">{{ tStatic('Вызов на дом') }}</span>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, reactive, watch,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import { EXPERIENCE_OPTIONS } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/constants'
import getDoctorsFilterHash from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getDoctorsFilterHash'
import type {
  DoctorsFilterState,
  LpuSpecOption,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'

const props = defineProps<{
  specialities: LpuSpecOption[]
  lpuDoctorsUrl: string
}>()

const isMobile = useIsMobile()
const selectMenuProps = {
  offsetY: true,
  maxHeight: 320,
}
const filter = reactive<DoctorsFilterState>({
  speciality: null,
  experience: '',
  gender: '',
  isHomeCall: false,
})

const experienceItems = computed(() => EXPERIENCE_OPTIONS.map(item => ({
  name: tStatic(item.name),
  value: item.value,
})))
const selectedGender = computed({
  get: () => filter.gender || undefined,
  set: (value?: string) => {
    filter.gender = value || ''
  },
})

function toggleHomeCall() {
  filter.isHomeCall = !filter.isHomeCall
}

function navigateByFilter() {
  const hash = getDoctorsFilterHash(filter)

  if (!hash || !process.env.IS_CLIENT) {
    return
  }

  window.location.href = `${props.lpuDoctorsUrl}${hash}`
}

watch(filter, navigateByFilter, { deep: true })
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.lpu-doctors-filter {
  &__select {
    :deep(.v-input__slot) {
      min-height: 48px;
    }

    :deep(.v-input__append-inner) {
      margin-top: 0;
      align-self: center;
    }

    :deep(input) {
      height: auto !important;
    }

    :deep(fieldset) {
      border-color: $color-deep-soft-blue;
    }

    :deep(.v-icon) {
      font-size: $ui-icon-fz-big;
      width: $ui-icon-fz-big;
      height: $ui-icon-fz-big;
      color: $ui-kit-text-secondary;
    }

    &--specialty {
      flex-grow: 1;
      min-width: 0;
    }

    &--experience {
      flex-shrink: 0;
      width: 183px;
    }
  }

  &__gender {
    flex-shrink: 0;
    overflow: hidden;
    height: 48px;
    border: 1px solid $color-deep-soft-blue;
    border-radius: $border-radius-sm;
    background-color: $ui-kit-bg-gray-0;

    :deep(.v-btn) {
      min-width: 56px;
      border: 0 !important;
      border-radius: 0;
      opacity: 1;
      background-color: $ui-kit-bg-gray-0;
      box-shadow: none;
    }

    :deep(.v-btn:not(:first-child)) {
      border-left: 1px solid $color-deep-soft-blue !important;
    }

    :deep(.v-btn--active) {
      background-color: $ui-kit-bg-gray-0 !important;
    }
  }

  &__home {
    flex-shrink: 0;
    overflow: hidden;
    height: 48px;
    border-radius: $border-radius-sm;
    background-color: $color-ultra-deep-grey;
    color: $color-dark-grey;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: darken($color-ultra-deep-grey, 5%);
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      box-shadow: inset 0 0 0 2px $ui-kit-primary;
    }
  }

  &__home-checkbox {
    flex-grow: 0;
    pointer-events: none;
    align-items: center;
    padding-top: 0;

    :deep(.v-input__control),
    :deep(.v-input__slot) {
      margin: 0;
      align-items: center;
    }
  }
}
</style>
